import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import useSWR from 'swr'

import { StatusBadge } from '@/components/ds'
import { Button } from '@/components/ui/button'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import API from '@/lib/api'

import { AvailableAccounts, BankConnectLayout, ConnectionNotice } from '../components/BankConnectLayout'

export default function ErsteBankPage() {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'bank',
  })
  const [busy, setBusy] = useState(false)

  const { data, isLoading, mutate } = useSWR<{
    data: {
      credentials: boolean
      connection: 'n/a' | 'failed' | 'success'
      availableAccounts: {
        accountId: string
        accountName: string
        accountNumber: string
        accountNumberPrefix: string | null
        bankCode: string
        iban: string
        bankBicCode: string | null
        friendlyName: string | null
        accountTypeId: string
        mainCurrency: string | null
      }[]
      knownAccounts: string[]
    }
  }>('/api/v2/erste-bank', apiFetcher)

  useSetPageHeader({
    breadcrumbs: [
      {
        href: '/bank',
        children: t('title'),
      },
      {
        href: '/bank/erste',
        children: t('erste.title'),
      },
    ],
    newButton: 'invoice',
  })

  const connect = () => {
    setBusy(true)
    API.post<{ data: { redirectUrl: string } }>('/api/v2/erste-bank')
      .then((response) => {
        window.location.href = response.data.data.redirectUrl
      })
      .catch(() => {
        toast.error('Propojení se nepodařilo spustit')
        setBusy(false)
      })
  }

  const disconnect = () => {
    setBusy(true)
    API.delete('/api/v2/erste-bank')
      .then(() => mutate())
      .finally(() => setBusy(false))
  }

  return (
    <>
      <title>{`${t('title')}, ${t('erste.title')}`}</title>

      <BankConnectLayout
        service="erste"
        title="Propojit Českou spořitelnu"
        description="Přístup potvrdíte přímo v internetovém bankovnictví, heslo k bance invo.space nevidí."
        steps={[t('erste.connect.step1'), t('erste.connect.step2'), t('erste.connect.step3')]}
        accounts={
          <AvailableAccounts
            loading={isLoading}
            accounts={data?.data.availableAccounts}
            knownAccounts={data?.data.knownAccounts}
            emptyText={data?.data.credentials ? 'Banka zatím nezpřístupnila žádné účty.' : 'Po propojení se tu ukážou účty, ke kterým jste přístup povolili.'}
            onAdd={(accountId) =>
              API.post(`/api/v2/erste-bank/accounts/${accountId}`, {
                accountId,
              }).then(() => mutate())
            }
          />
        }
      >
        <ConnectionNotice connection={data?.data.connection} />
        {data?.data.credentials ? (
          <div className="space-y-3">
            <StatusBadge status="matched" label="Propojeno" />
            <p className="text-sm">{t('erste.credentials.set')}</p>
            <Button variant="outline" size="sm" onClick={disconnect} disabled={busy}>
              {t('erste.disconnect.button')}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <h2 className="text-sm font-medium">Přihlášení do banky</h2>
            <p className="text-xs text-muted-foreground">Přesměrujeme vás do bankovnictví České spořitelny a po potvrzení zpět sem.</p>
            <Button onClick={connect} disabled={busy || isLoading}>
              {t('erste.connect.button')}
            </Button>
          </div>
        )}
      </BankConnectLayout>
    </>
  )
}
